import { useEffect, useState, useCallback } from "react";
import PortalLayout from "@/layouts/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Clock, Video, MapPin, Trash2, Send, Users, X, Save, Mail, RefreshCw, Edit3 } from "lucide-react";
import { BulkActionsBar, BulkCheckbox } from "@/components/admin/BulkActionsBar";

const STATUS_BADGE = {
  requested: "bg-amber-100 text-amber-800",
  confirmed: "bg-emerald-100 text-emerald-800",
  done: "bg-neutral-200 text-neutral-700",
  cancelled: "bg-rose-100 text-rose-700",
};
const STATUS_LABEL = { requested: "Demande", confirmed: "Confirmée", done: "Terminée", cancelled: "Annulée" };

export default function AdminMeetings() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (filter !== "all") params.status = filter;
    api.get("/admin/meetings", { params }).then((r) => setList(r.data)).catch(() => {});
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const del = async (m) => {
    if (!window.confirm(`Supprimer la réunion "${m.title}" ?`)) return;
    try { await api.delete(`/admin/meetings/${m.id}`); toast.success("Réunion supprimée"); load(); }
    catch { toast.error("Erreur"); }
  };

  // Bulk selection
  const [selected, setSelected] = useState(new Set());
  const toggleSel = (id) => { const ns = new Set(selected); if (ns.has(id)) ns.delete(id); else ns.add(id); setSelected(ns); };
  const selectAll = () => setSelected(new Set(list.map((m) => m.id)));
  const clearSel = () => setSelected(new Set());
  const bulkDelete = async () => {
    if (!window.confirm(`⚠️ SUPPRIMER ${selected.size} réunion${selected.size > 1 ? "s" : ""} ?\nCette action est IRRÉVERSIBLE.`)) return;
    try {
      const r = await api.post("/admin/meetings/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${r.data.deleted} réunion${r.data.deleted > 1 ? "s" : ""} supprimée${r.data.deleted > 1 ? "s" : ""}`);
      clearSel();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    }
  };

  const resend = async (m) => {
    try { await api.post(`/admin/meetings/${m.id}/send-invite`); toast.success("Email de rappel envoyé"); }
    catch { toast.error("Erreur d'envoi"); }
  };

  return (
    <PortalLayout sidebar="admin">
      <div className="space-y-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-serif text-4xl text-navy">Réunions</h1>
            <p className="mt-1 text-sm text-neutral-600">Planifier et suivre les RDV avec membres, prospects et contacts externes.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={load} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-neutral-200 px-3 py-2 text-xs font-bold text-neutral-700 hover:bg-neutral-50" data-testid="refresh-meetings"><RefreshCw className="h-3 w-3" /></button>
            <button onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 rounded-full bg-wine-600 px-4 py-2 text-xs font-bold text-white hover:bg-wine-700" data-testid="new-meeting-btn">
              <Plus className="h-3.5 w-3.5" /> Nouvelle réunion
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {[["all", "Toutes"], ["requested", "Demandes"], ["confirmed", "Confirmées"], ["done", "Terminées"], ["cancelled", "Annulées"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} className={`rounded-full px-4 py-2 text-xs font-semibold ${filter === k ? "bg-wine-500 text-white" : "bg-white text-neutral-600 border border-neutral-200"}`} data-testid={`meetings-filter-${k}`}>{l}</button>
          ))}
        </div>

        {selected.size > 0 && (
          <BulkActionsBar
            count={selected.size}
            total={list.length}
            onClear={clearSel}
            onSelectAll={selectAll}
            onDelete={bulkDelete}
            label="réunion"
            testId="admin-meetings-bulk"
          />
        )}

        <div className="overflow-hidden rounded-2xl border border-neutral-100 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-wine-50/40 text-xs uppercase tracking-wider text-neutral-500">
              <tr><th className="px-3 py-3 w-8"><BulkCheckbox checked={list.length > 0 && selected.size === list.length} onChange={(v) => v ? selectAll() : clearSel()} testId="meetings-select-all" /></th><th className="px-5 py-3 text-left">Quand</th><th className="px-5 py-3 text-left">Titre</th><th className="px-5 py-3 text-left">Participants</th><th className="px-5 py-3 text-left">Lieu / Visio</th><th className="px-5 py-3 text-left">Statut</th><th className="px-5 py-3 text-left">Source</th><th className="px-5 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {list.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-sm text-neutral-500">Aucune réunion. Cliquez « Nouvelle réunion » pour commencer.</td></tr>}
              {list.map((m) => (
                <tr key={m.id} className={`border-t border-neutral-100 hover:bg-wine-50/30 ${selected.has(m.id) ? "bg-rose-50/40" : ""}`} data-testid={`meeting-row-${m.id}`}>
                  <td className="px-3 py-3"><BulkCheckbox checked={selected.has(m.id)} onChange={() => toggleSel(m.id)} testId={`meeting-select-${m.id}`} /></td>
                  <td className="px-5 py-3">
                    <div className="font-semibold text-neutral-900">{m.scheduled_at && new Date(m.scheduled_at).toLocaleDateString("fr", { day: "numeric", month: "short", year: "numeric" })}</div>
                    <div className="text-xs text-neutral-500"><Clock className="mr-1 inline h-3 w-3" />{m.scheduled_at && new Date(m.scheduled_at).toLocaleTimeString("fr", { hour: "2-digit", minute: "2-digit" })} · {m.duration_min} min</div>
                  </td>
                  <td className="px-5 py-3"><div className="font-semibold text-neutral-900">{m.title}</div>{m.notes && <div className="text-xs text-neutral-500 line-clamp-1">{m.notes}</div>}</td>
                  <td className="px-5 py-3">
                    {(m.attendees || []).slice(0, 2).map((a, i) => (
                      <div key={i} className="text-xs"><span className="font-semibold text-neutral-800">{a.name || a.email}</span>{a.company && <span className="text-neutral-500"> · {a.company}</span>}</div>
                    ))}
                    {(m.attendees || []).length > 2 && <div className="text-[10px] text-neutral-400">+{m.attendees.length - 2} autre(s)</div>}
                  </td>
                  <td className="px-5 py-3 text-xs">
                    {m.location && <div className="text-neutral-700"><MapPin className="mr-1 inline h-3 w-3" />{m.location}</div>}
                    {m.meeting_url && <a href={m.meeting_url} target="_blank" rel="noopener noreferrer" className="text-wine-600 hover:underline"><Video className="mr-1 inline h-3 w-3" />Lien visio</a>}
                    {!m.location && !m.meeting_url && <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="px-5 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_BADGE[m.status] || "bg-neutral-100 text-neutral-700"}`}>{STATUS_LABEL[m.status] || m.status}</span></td>
                  <td className="px-5 py-3 text-xs text-neutral-500">{m.source === "public_request" ? "Demande publique" : "Admin"}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => setEditing(m)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-blue-50 hover:text-blue-700" title="Modifier" data-testid={`meeting-edit-${m.id}`}><Edit3 className="h-3.5 w-3.5" /></button>
                      <button onClick={() => resend(m)} className="grid h-7 w-7 place-items-center rounded-full text-blue-600 hover:bg-blue-50" title="Renvoyer l'invitation" data-testid={`meeting-resend-${m.id}`}><Send className="h-3.5 w-3.5" /></button>
                      <button onClick={() => del(m)} className="grid h-7 w-7 place-items-center rounded-full text-rose-600 hover:bg-rose-50" title="Supprimer" data-testid={`meeting-delete-${m.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {(creating || editing) && <MeetingModal initial={editing} onClose={() => { setCreating(false); setEditing(null); }} onSaved={() => { setCreating(false); setEditing(null); load(); }} />}
    </PortalLayout>
  );
}

function MeetingModal({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(initial ? {
    ...initial,
    scheduled_at: initial.scheduled_at ? new Date(initial.scheduled_at).toISOString().slice(0, 16) : "",
  } : {
    title: "", scheduled_at: "", duration_min: 30, location: "", meeting_url: "", notes: "",
    attendees: [], notify: true, status: "confirmed",
  });
  const [newAtt, setNewAtt] = useState({ email: "", name: "", phone: "", company: "" });
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (search.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const [c, m, p] = await Promise.all([
          api.get("/admin/contacts", { params: { q: search, limit: 6 } }),
          api.get("/admin/companies", { params: { q: search, limit: 4 } }),
          api.get("/admin/prospects", { params: { q: search, limit: 4 } }),
        ]);
        const cArr = (Array.isArray(c.data) ? c.data : (c.data?.items || [])).map((x) => ({ kind: "contact", email: x.email, name: `${x.first_name || ""} ${x.last_name || ""}`.trim(), company: x.company_name || "", phone: x.phone || "" }));
        const mArr = (Array.isArray(m.data) ? m.data : (m.data?.items || [])).map((x) => ({ kind: "member", email: x.email, name: x.name, company: x.name, phone: x.phone || "" }));
        const pArr = (Array.isArray(p.data) ? p.data : (p.data?.items || [])).map((x) => ({ kind: "prospect", email: x.contact_email, name: x.contact_name, company: x.company_name || "", phone: "" }));
        setResults([...cArr, ...mArr, ...pArr].filter((r) => r.email));
      } catch { setResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const pick = (r) => {
    if (form.attendees.find((a) => a.email === r.email)) { toast.info("Déjà ajouté"); return; }
    setForm({ ...form, attendees: [...form.attendees, { email: r.email, name: r.name, company: r.company, phone: r.phone }] });
    setSearch(""); setResults([]);
  };

  const addManualAtt = () => {
    if (!newAtt.email) return;
    if (form.attendees.find((a) => a.email === newAtt.email)) { toast.info("Déjà ajouté"); return; }
    setForm({ ...form, attendees: [...form.attendees, { ...newAtt }] });
    setNewAtt({ email: "", name: "", phone: "", company: "" });
  };

  const removeAtt = (i) => setForm({ ...form, attendees: form.attendees.filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!form.title.trim()) { toast.error("Titre requis"); return; }
    if (!form.scheduled_at) { toast.error("Date requise"); return; }
    setSaving(true);
    try {
      const payload = { ...form, scheduled_at: new Date(form.scheduled_at).toISOString() };
      if (isEdit) {
        await api.patch(`/admin/meetings/${initial.id}`, { ...payload, notify_update: payload.notify });
        toast.success("Réunion mise à jour");
      } else {
        await api.post("/admin/meetings", payload);
        toast.success("Réunion créée" + (payload.notify ? " · invitations envoyées" : ""));
      }
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[90vh] overflow-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="meeting-modal">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-navy">{isEdit ? "Modifier la réunion" : "Nouvelle réunion"}</h2>
          <button onClick={onClose} className="grid h-8 w-8 place-items-center rounded-full hover:bg-neutral-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre de la réunion *" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="meeting-title" />
          <div className="grid gap-3 sm:grid-cols-3">
            <input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} className="sm:col-span-2 rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="meeting-when" />
            <input type="number" value={form.duration_min} min={5} max={480} onChange={(e) => setForm({ ...form, duration_min: parseInt(e.target.value) || 30 })} placeholder="Durée (min)" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="meeting-duration" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Lieu (ex: 12 rue de la Paix, Paris)" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="meeting-location" />
            <input type="url" value={form.meeting_url} onChange={(e) => setForm({ ...form, meeting_url: e.target.value })} placeholder="Lien visio (Zoom, Teams, Meet…)" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="meeting-url" />
          </div>
          {isEdit && (
            <select value={form.status || "confirmed"} onChange={(e) => setForm({ ...form, status: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="meeting-status">
              <option value="requested">Demande</option>
              <option value="confirmed">Confirmée</option>
              <option value="done">Terminée</option>
              <option value="cancelled">Annulée</option>
            </select>
          )}
          <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes / ordre du jour" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" />

          <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-700"><Users className="mr-1 inline h-3 w-3" /> Participants</div>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="🔎 Chercher dans contacts / membres / prospects (2+ caractères)…" className="mt-2 w-full rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm" data-testid="meeting-search-att" />
            {results.length > 0 && (
              <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-indigo-100 bg-white">
                {results.map((r, i) => (
                  <button key={i} type="button" onClick={() => pick(r)} className="block w-full border-t border-neutral-50 px-3 py-2 text-left text-xs hover:bg-indigo-50/60" data-testid={`meeting-pick-${i}`}>
                    <span className="font-semibold">{r.name || r.email}</span> <span className="text-neutral-400">({r.kind})</span> <span className="text-neutral-500">{r.email}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="mt-2 grid gap-1 sm:grid-cols-5">
              <input value={newAtt.email} onChange={(e) => setNewAtt({ ...newAtt, email: e.target.value })} placeholder="Email" className="sm:col-span-2 rounded-lg border border-indigo-200 bg-white px-2 py-1.5 text-xs" data-testid="meeting-new-att-email" />
              <input value={newAtt.name} onChange={(e) => setNewAtt({ ...newAtt, name: e.target.value })} placeholder="Nom" className="rounded-lg border border-indigo-200 bg-white px-2 py-1.5 text-xs" />
              <input value={newAtt.company} onChange={(e) => setNewAtt({ ...newAtt, company: e.target.value })} placeholder="Société" className="rounded-lg border border-indigo-200 bg-white px-2 py-1.5 text-xs" />
              <button type="button" onClick={addManualAtt} className="rounded-lg bg-indigo-500 px-2 py-1.5 text-xs font-bold text-white hover:bg-indigo-600" data-testid="meeting-add-att-manual">+ Ajouter</button>
            </div>
            {form.attendees.length > 0 && (
              <div className="mt-2 space-y-1">
                {form.attendees.map((a, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg bg-white px-2 py-1.5 text-xs" data-testid={`meeting-att-row-${i}`}>
                    <div><span className="font-semibold">{a.name || a.email}</span> <span className="text-neutral-500">{a.email}{a.company ? ` · ${a.company}` : ""}</span></div>
                    <button type="button" onClick={() => removeAtt(i)} className="text-rose-500 hover:underline">Retirer</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm text-neutral-700">
            <input type="checkbox" checked={!!form.notify} onChange={(e) => setForm({ ...form, notify: e.target.checked })} className="accent-wine-500" data-testid="meeting-notify" />
            <Mail className="h-4 w-4 text-wine-600" />
            {isEdit ? "Envoyer un email de mise à jour aux participants" : "Envoyer l'invitation par email aux participants"}
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="rounded-full border border-neutral-300 bg-white px-4 py-2 text-xs font-bold text-neutral-700">Annuler</button>
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 rounded-full bg-wine-600 px-5 py-2 text-xs font-bold text-white hover:bg-wine-700 disabled:opacity-50" data-testid="meeting-save"><Save className="h-3.5 w-3.5" /> {saving ? "Sauvegarde…" : (isEdit ? "Mettre à jour" : "Créer & inviter")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
